import { useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";
import useLanguage from "@/hooks/useLanguage";

/**
 * Header-level language switcher. Small globe button → dropdown listing each
 * language in its OWN script (हिन्दी, தமிழ், Español, …). Active language
 * gets a gold check + brighter row.
 *
 * Variants:
 *   - `variant="light"` for the white-surface landing header
 *   - default                 for the dark cosmic header / sidebar
 */
export default function LanguageSwitcher({ variant = "dark", testId = "lang-switcher" }) {
    const { code, languages, setLanguage } = useLanguage();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    // Close when clicking outside or pressing Esc — standard dropdown contract.
    useEffect(() => {
        if (!open) return;
        function handleClick(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        function handleKey(e) { if (e.key === "Escape") setOpen(false); }
        document.addEventListener("mousedown", handleClick);
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("mousedown", handleClick);
            document.removeEventListener("keydown", handleKey);
        };
    }, [open]);

    const isLight = variant === "light";
    const triggerCls = isLight
        ? "text-stone-700 hover:text-aurora-700 border border-stone-200 hover:border-aurora-300"
        : "text-white/70 hover:text-white border border-white/[0.08] hover:border-gold/40";

    const activeCode = (languages.find((l) => l.code === code) || languages[0]);

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={activeCode?.name_native ? `Language: ${activeCode.name_native}` : "Language"}
                data-testid={testId}
                className={`inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1.5 rounded-full transition-colors ${triggerCls}`}
            >
                <Globe className="w-3.5 h-3.5" strokeWidth={1.6} />
                <span className="hidden sm:inline">{activeCode?.name_native || "Language"}</span>
            </button>

            {open && (
                <div
                    role="listbox"
                    data-testid={`${testId}-menu`}
                    className={`absolute right-0 mt-2 w-44 z-50 rounded-md overflow-hidden shadow-lg ${
                        isLight
                            ? "bg-white border border-stone-200"
                            : "bg-lodestar-midnight/95 backdrop-blur border border-lodestar-imperial/40"
                    }`}
                >
                    {languages.map((lang) => {
                        const active = lang.code === code;
                        return (
                            <button
                                key={lang.code}
                                type="button"
                                role="option"
                                aria-selected={active}
                                onClick={() => { setLanguage(lang.code); setOpen(false); }}
                                data-testid={`${testId}-option-${lang.code}`}
                                className={`w-full px-3 py-2 text-[12.5px] flex items-center justify-between text-left transition-colors ${
                                    isLight
                                        ? active
                                            ? "bg-aurora-50 text-aurora-800"
                                            : "text-stone-700 hover:bg-stone-50"
                                        : active
                                            ? "bg-gradient-to-r from-lodestar-gold-soft/10 to-transparent text-lodestar-white-pearl"
                                            : "text-lodestar-white-mist/80 hover:bg-white/[0.04] hover:text-lodestar-white-pearl"
                                }`}
                            >
                                <span>{lang.name_native}</span>
                                {active && <Check className="w-3.5 h-3.5 text-lodestar-gold-soft" strokeWidth={2} />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
